import type { Elplan } from 'api/elplan/elplan.api'
import Datetime from 'utils/datetime/datetime'
import { getDayHourStarts, getDayQuarterStarts } from 'utils/datetime/dayTimeSteps'
import { applyElplanSlotEdits, toStoredSlotEdits } from './draft'
import type {
  ElplanDraft,
  ElplanEditorSession,
  ElplanSaveTarget,
  ElplanSessionSource,
  ElplanSlotUnit,
} from './ElplanEditor.types'
import { roundElplanVolumeMean, toElplanVolumeCellText } from './volume'

type BidSumPoint = { start: ISODateTime; volumeMw: number | null }

const DAY_STEP_STARTS: Record<ElplanSlotUnit, (instant: ISODateTime) => ISODateTime[]> = {
  quarter: getDayQuarterStarts,
  hour: getDayHourStarts,
}

export function recogniseElplanResolution(starts: readonly string[], day: ISODateTime): ElplanSlotUnit | null {
  const canonicalStarts: ISODateTime[] = []
  for (const start of starts) {
    const canonicalStart = toElplanStart(start)
    if (canonicalStart === null) {
      return null
    }
    canonicalStarts.push(canonicalStart)
  }
  canonicalStarts.sort(Datetime.compare)

  const units: ElplanSlotUnit[] = ['quarter', 'hour']
  return units.find((unit) => isSameSequence(canonicalStarts, DAY_STEP_STARTS[unit](day))) ?? null
}

export function openElplanEditorSession(source: ElplanSessionSource, day: ISODateTime): ElplanEditorSession | null {
  switch (source.kind) {
    case 'empty':
      return { seed: toZeroQuarterDraft(day), target: toCreateTarget(source.elplanInfoId, day) }
    case 'fromBids':
      return openFromBidsSession(source.elplanInfoId, source.bidSumMwDataset, day)
    case 'edit':
      return openEditSession(source.elplan, day)
  }
}

function openFromBidsSession(
  elplanInfoId: number,
  bidSumMwDataset: Pick<Dataset, 'times' | 'values'>,
  day: ISODateTime
): ElplanEditorSession | null {
  const points = readBidSumPointsOfDay(bidSumMwDataset, day)
  const seed = points && seedQuarterDraftFromBidSum(points, day)
  if (!seed) {
    return null
  }

  return { seed, target: toCreateTarget(elplanInfoId, day) }
}

function readBidSumPointsOfDay(
  bidSumMwDataset: Pick<Dataset, 'times' | 'values'>,
  day: ISODateTime
): BidSumPoint[] | null {
  const dayStart = Datetime.getStartOfDay(day)
  const nextDayStart = Datetime.getDayAfterDate(dayStart).startTime
  const times: readonly (string | number)[] = bidSumMwDataset.times
  const points: BidSumPoint[] = []
  const seenStarts = new Set<ISODateTime>()

  for (const [index, time] of times.entries()) {
    const start = toElplanStart(time)
    if (start === null) {
      return null
    }
    if (Datetime.isBefore(start, dayStart) || Datetime.isAfterOrEqual(start, nextDayStart)) {
      continue
    }

    const volumeMw = bidSumMwDataset.values[index] ?? null
    if (seenStarts.has(start) || (volumeMw !== null && !Number.isFinite(volumeMw))) {
      return null
    }
    seenStarts.add(start)
    points.push({ start, volumeMw })
  }

  return points
}

function seedQuarterDraftFromBidSum(points: readonly BidSumPoint[], day: ISODateTime): ElplanDraft | null {
  const hourStarts = new Set(getDayHourStarts(day))
  const quarterStarts = new Set(getDayQuarterStarts(day))
  const unit: ElplanSlotUnit = points.every(({ start }) => hourStarts.has(start)) ? 'hour' : 'quarter'
  if (unit === 'quarter' && points.some(({ start }) => !quarterStarts.has(start))) {
    return null
  }

  const zeroDraft = toZeroQuarterDraft(day)
  const edits = points.flatMap(({ start, volumeMw }) =>
    volumeMw === null
      ? []
      : toStoredSlotEdits({ unit, start }, toElplanVolumeCellText(roundElplanVolumeMean([volumeMw])), zeroDraft)
  )

  return applyElplanSlotEdits(zeroDraft, edits)
}

function openEditSession(elplan: Pick<Elplan, 'id' | 'elplan_values'>, day: ISODateTime): ElplanEditorSession | null {
  const storedValues: { id: number; start: ISODateTime; volumeMw: number }[] = []
  for (const { id, start_time, volume } of elplan.elplan_values) {
    const start = toElplanStart(start_time)
    if (id === null || id === undefined || start === null || !Number.isFinite(volume)) {
      return null
    }
    storedValues.push({ id, start, volumeMw: volume })
  }
  if (new Set(storedValues.map(({ id }) => id)).size !== storedValues.length) {
    return null
  }

  const resolution = recogniseElplanResolution(
    storedValues.map(({ start }) => start),
    day
  )
  if (resolution === null) {
    return null
  }

  return {
    seed: {
      resolution,
      slotStarts: DAY_STEP_STARTS[resolution](day),
      textByStart: Object.fromEntries(
        storedValues.map(({ start, volumeMw }) => [start, toElplanVolumeCellText(volumeMw)])
      ),
    },
    target: {
      kind: 'update',
      elplanId: elplan.id,
      valueIdByStart: Object.fromEntries(storedValues.map(({ start, id }) => [start, id])),
    },
  }
}

function toZeroQuarterDraft(day: ISODateTime): ElplanDraft {
  const slotStarts = getDayQuarterStarts(day)
  const zeroText = toElplanVolumeCellText(0)
  return {
    resolution: 'quarter',
    slotStarts,
    textByStart: Object.fromEntries(slotStarts.map((start) => [start, zeroText])),
  }
}

function toCreateTarget(elplanInfoId: number, day: ISODateTime): ElplanSaveTarget {
  return { kind: 'create', elplanInfoId, day: Datetime.getStartOfDay(day) }
}

function toElplanStart(time: string | number): ISODateTime | null {
  return Number.isNaN(new Date(time).getTime()) ? null : Datetime.toISOString(time)
}

function isSameSequence(a: readonly ISODateTime[], b: readonly ISODateTime[]): boolean {
  return a.length === b.length && a.every((start, index) => start === b[index])
}
